/**
 * SVG text rendering utilities for BPMN shapes.
 *
 * Uses a hidden SVG element + getBBox() for accurate pixel-width measurement,
 * matching bpmn-js / diagram-js text rendering quality.
 * Word-break prefers semantic boundaries (spaces) and falls back to
 * character-level splitting only for words wider than the available space.
 */

import { getThemeStyle } from '../theme';

const FONT_FAMILY = '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const FONT_SIZE = 12;
const LINE_HEIGHT = 14;

// ── Measurement helpers ────────────────────────────────────────────────────

let _measureEl = null;

/**
 * Return (or lazily create) a hidden <text> element in a fixed SVG used
 * solely for getBBox() measurements.  Same pattern as diagram-js.
 */
function getMeasureEl() {
  if (_measureEl && document.body.contains(_measureEl)) return _measureEl;

  let svg = document.getElementById('epx-text-measure-svg');
  if (!svg) {
    svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.id = 'epx-text-measure-svg';
    svg.style.cssText =
      'visibility:hidden;position:fixed;top:0;left:0;width:0;height:0;overflow:hidden;pointer-events:none;';
    document.body.appendChild(svg);
  }

  _measureEl = svg.querySelector('text');
  if (!_measureEl) {
    _measureEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    svg.appendChild(_measureEl);
  }
  return _measureEl;
}

/**
 * Measure the rendered pixel width of a string at the given font settings.
 * Falls back to character-count estimate when getBBox() is unavailable (SSR).
 */
function measureWidth(text, fontSize, fontFamily) {
  if (typeof document === 'undefined' || !text) return text.length * fontSize * 0.58;
  try {
    const el = getMeasureEl();
    el.style.fontSize = `${fontSize}px`;
    el.style.fontFamily = fontFamily;
    el.textContent = text;
    return el.getBBox().width;
  } catch (_) {
    return text.length * fontSize * 0.58;
  }
}

// ── Word-wrapping ──────────────────────────────────────────────────────────

/**
 * Wrap text into lines that each fit within maxWidth pixels.
 *
 * Algorithm (mirrors bpmn-js / diagram-js):
 *  1. Split on whitespace into words.
 *  2. Accumulate words onto the current line while the line fits.
 *  3. When a word would overflow: flush the current line and start a new one.
 *  4. If a single word is wider than maxWidth, force-break it at the last
 *     character that still fits (character-level fallback).
 */
function wrapText(text, maxWidth, fontSize = FONT_SIZE, fontFamily = FONT_FAMILY) {
  if (!text) return [];

  const lines = [];
  const words = text.trim().split(/\s+/);
  let currentLine = '';

  for (const word of words) {
    if (!word) continue;

    const candidate = currentLine ? `${currentLine} ${word}` : word;

    if (measureWidth(candidate, fontSize, fontFamily) <= maxWidth) {
      // Fits → keep building the line
      currentLine = candidate;
    } else if (!currentLine) {
      // Single word is already wider than maxWidth → character-level break
      let partial = '';
      for (const ch of word) {
        const test = partial + ch;
        if (measureWidth(test, fontSize, fontFamily) <= maxWidth) {
          partial = test;
        } else {
          if (partial) lines.push(partial);
          partial = ch;
        }
      }
      currentLine = partial;
    } else {
      // Word doesn't fit on current line → flush and start fresh
      lines.push(currentLine);
      currentLine = word;
    }
  }

  if (currentLine) lines.push(currentLine);
  return lines;
}

// ── Renderers ─────────────────────────────────────────────────────────────

/**
 * Render wrapped SVG <text> with <tspan> elements inside a shape.
 *
 * @param {Function} h - Virtual DOM createElement (LogicFlow's h)
 * @param {string} text - The text to render
 * @param {number} cx - Center X of the text area
 * @param {number} cy - Center Y of the text area
 * @param {number} maxWidth - Maximum width for wrapping
 * @param {object} [opts] - Options
 * @param {number} [opts.fontSize] - Font size (default 12)
 * @param {number} [opts.lineHeight] - Line height (default 14)
 * @param {string} [opts.fill] - Text color (default '#333')
 * @param {string} [opts.fontWeight] - Font weight (default 'normal')
 * @returns {VNode} SVG <text> element with <tspan> children
 */
export function renderWrappedText(h, text, cx, cy, maxWidth, opts = {}) {
  const fontSize   = opts.fontSize   || FONT_SIZE;
  const lineHeight = opts.lineHeight || LINE_HEIGHT;
  const fontFamily = FONT_FAMILY;
  const themeFill  = opts.graphModel ? getThemeStyle(opts.graphModel, 'text').fill : null;
  const fill       = opts.fill || themeFill || '#333';
  const fontWeight = opts.fontWeight || 'normal';

  const lines = wrapText(text, maxWidth, fontSize, fontFamily);
  if (lines.length === 0) return h('g', {});

  // Vertically centre the text block around cy
  const totalHeight = (lines.length - 1) * lineHeight;
  const startY = cy - totalHeight / 2;

  const tspans = lines.map((line, i) =>
    h('tspan', {
      x: cx,
      dy: i === 0 ? (startY - cy) : lineHeight,
    }, line)
  );

  return h('text', {
    x: cx,
    y: cy,
    dy: '0.35em',
    fill,
    fontSize,
    fontWeight,
    fontFamily,
    textAnchor: 'middle',
    pointerEvents: 'none',
    'data-epx': '1',
  }, tspans);
}

/**
 * Render wrapped SVG text rotated -90° (for vertical headers like Participant/Lane).
 */
export function renderRotatedText(h, text, cx, cy, maxHeight, opts = {}) {
  const fontSize   = opts.fontSize   || FONT_SIZE;
  const lineHeight = opts.lineHeight || LINE_HEIGHT;
  const fontFamily = FONT_FAMILY;
  const themeFill  = opts.graphModel ? getThemeStyle(opts.graphModel, 'text').fill : null;
  const fill       = opts.fill || themeFill || '#333';
  const fontWeight = opts.fontWeight || 'bold';

  const lines = wrapText(text, maxHeight, fontSize, fontFamily);
  if (lines.length === 0) return h('g', {});

  const totalHeight = (lines.length - 1) * lineHeight;
  const startDy = -totalHeight / 2;

  const tspans = lines.map((line, i) =>
    h('tspan', {
      x: 0,
      dy: i === 0 ? startDy : lineHeight,
    }, line)
  );

  // The transform is placed on the <text> itself (not a wrapping <g>) so that
  // cloning the text into EpxFlow's text layer preserves its position.
  return h('text', {
    x: 0,
    y: 0,
    transform: `translate(${cx}, ${cy}) rotate(-90)`,
    fill,
    fontSize,
    fontWeight,
    fontFamily,
    textAnchor: 'middle',
    dominantBaseline: 'central',
    pointerEvents: 'none',
    'data-epx': '1',
  }, tspans);
}

/**
 * Apply a default below-shape label offset only when no explicit position was
 * loaded from a BPMNLabel element.  When data.text carries explicit y coords
 * (restored by BpmnReader from BPMNLabel bounds) we preserve them so the user's
 * saved or bpmn-js-authored positioning is respected.
 *
 * Call this from initNodeData() in event and gateway models:
 *   initLabelOffset(this, data, this.r + 14);   // circles
 *   initLabelOffset(this, data, this.ry + 18);  // diamonds
 */
export function initLabelOffset(model, data, offsetY) {
  if (!model.text || !model.text.value) return;
  // Always place below the shape — auto-layout and the node's own offsetY
  // are the source of truth.  BPMNLabel coordinates from the XML are NOT used
  // here because they may have been written with a previous incorrect offset
  // and would place the label inside the shape.
  model.text = {
    ...model.text,
    draggable: true,
    y: model.y + offsetY,
  };
}

/**
 * Estimate BPMNLabel bounds for a text string.
 * Uses character-width heuristic (no DOM needed — safe for serialization context).
 * Returns { width, height } in pixels.
 *
 * @param {string} text
 * @param {number} [maxWidth=150] - wrapping width
 */
export function estimateLabelBounds(text, maxWidth = 150) {
  if (!text) return { width: 0, height: 0 };
  const AVG_CHAR_W = 6.5;
  const rawWidth = text.length * AVG_CHAR_W;
  const width = Math.min(Math.max(rawWidth, 30), maxWidth);
  const lines = Math.ceil(rawWidth / maxWidth);
  const height = lines * LINE_HEIGHT + 4;
  return { width, height };
}

export { FONT_FAMILY, FONT_SIZE, LINE_HEIGHT };
